import type { Condition } from '@databink/core/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { RecordTable } from '../components/record-table.js';
import {
  buttonClass,
  Card,
  cn,
  ErrorBanner,
  Input,
  NativeSelect,
  PageHeader,
  Spinner,
} from '../components/ui.js';
import type { ListFilter, ScreenRecord } from '../extension-types.js';
import { fieldOf, listColumns, useAppMeta, useEntity } from '../lib/meta.js';
import { useSlot } from '../lib/slots.js';
import { errorMessage, trpc } from '../lib/trpc.js';

export interface ListSearch {
  q?: string;
  view?: string;
  state?: 'open' | 'done' | 'canceled';
  page?: number;
  sort?: string;
}

const PAGE = 50;

export function validateListSearch(search: Record<string, unknown>): ListSearch {
  const out: ListSearch = {};
  if (typeof search['q'] === 'string' && search['q']) out.q = search['q'];
  if (typeof search['view'] === 'string' && search['view']) out.view = search['view'];
  if (search['state'] === 'open' || search['state'] === 'done' || search['state'] === 'canceled')
    out.state = search['state'];
  const page = Number(search['page']);
  if (Number.isInteger(page) && page > 1) out.page = page;
  if (typeof search['sort'] === 'string' && /^-?[a-z_]+$/.test(search['sort']))
    out.sort = search['sort'];
  return out;
}

/** Every entity's list screen: columns, filters and views come from metadata and configuration. */
export function EntityList() {
  const { entity: entityId } = useParams({ strict: false }) as { entity: string };
  const search = useSearch({ strict: false }) as ListSearch;
  const navigate = useNavigate();
  const meta = useAppMeta();
  const entity = useEntity(entityId);
  const listActions = useSlot(`${entityId}.list.actions` as `${'crm.party'}.list.actions`);
  const views = meta.saved_views.filter((v) => v.entity === entityId);
  const view = views.find((v) => v.key === search.view) ?? null;
  const [text, setText] = useState(search.q ?? '');
  // Cards have no column headings to click, so the list sorts from a picker instead.
  const [stacked, setStacked] = useState(false);

  const go = (next: { [K in keyof ListSearch]?: ListSearch[K] | undefined }) =>
    void navigate({
      to: '/e/$entity',
      params: { entity: entityId },
      search: (current: ListSearch) => validateListSearch({ ...current, page: undefined, ...next }),
    });

  useEffect(() => setText(search.q ?? ''), [entityId, search.q]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if ((search.q ?? '') !== text) go({ q: text || undefined });
    }, 300);
    return () => clearTimeout(timer);
    // Only typing starts the timer; `go` and the current search are read when it fires.
  }, [text]);

  const sortKey =
    search.sort ?? (view?.sort ? `${view.sort.dir === 'desc' ? '-' : ''}${view.sort.field}` : null);
  const sort = sortKey
    ? {
        field: sortKey.replace(/^-/, ''),
        dir: sortKey.startsWith('-') ? ('desc' as const) : ('asc' as const),
      }
    : (entity.sort ?? null);
  const page = search.page ?? 1;
  const hasStates = Object.keys(entity.states).length > 0;
  const query: ListFilter = {
    ...(view && { filter: view.filters as Condition[] }),
    ...(search.q && { search: search.q }),
    ...(sort && { sort }),
    ...(search.state && { state_category: [search.state] }),
  };
  const input = { entity: entityId, limit: PAGE, offset: (page - 1) * PAGE, ...query };
  const list = useQuery({
    ...trpc.records.list.queryOptions(input),
    placeholderData: (previous) => previous,
  });
  const columns = listColumns(meta, entity, view?.columns ?? null);
  const total = list.data?.total ?? 0;

  return (
    <>
      <PageHeader
        title={entity.plural}
        subtitle={entity.help}
        actions={
          <>
            {listActions.map((a) => (
              <a.component key={a.id} entity={entity} query={query} />
            ))}
            {entity.access.create && (
              <Link
                to="/e/$entity/new"
                params={{ entity: entityId }}
                className={buttonClass('primary')}
              >
                <Plus className="size-4" aria-hidden /> New {entity.label.toLowerCase()}
              </Link>
            )}
          </>
        }
      />
      <Card padded={false}>
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 p-3">
          <div className="relative w-full sm:max-w-xs">
            <Search
              className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-slate-400"
              aria-hidden
            />
            <Input
              aria-label={`Search ${entity.plural.toLowerCase()}`}
              placeholder="Search"
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="pl-8"
            />
          </div>
          {hasStates && (
            <div
              className="flex rounded-md border border-slate-200 p-0.5 text-sm"
              role="group"
              aria-label="Filter by state"
            >
              {([undefined, 'open', 'done', 'canceled'] as const).map((s) => (
                <button
                  key={s ?? 'all'}
                  type="button"
                  aria-pressed={search.state === s}
                  onClick={() => go({ state: s })}
                  className={cn(
                    'rounded px-2.5 py-1 text-slate-600',
                    search.state === s && 'bg-slate-100 font-medium text-slate-900',
                  )}
                >
                  {s === undefined
                    ? 'All'
                    : s === 'open'
                      ? 'Open'
                      : s === 'done'
                        ? 'Done'
                        : 'Canceled'}
                </button>
              ))}
            </div>
          )}
          {views.length > 0 && (
            <div className="w-full sm:w-60">
              <NativeSelect
                aria-label="Saved view"
                value={search.view ?? ''}
                onChange={(e) => go({ view: e.target.value || undefined, sort: undefined })}
              >
                <option value="">All {entity.plural.toLowerCase()}</option>
                {views.map((v) => (
                  <option key={v.key} value={v.key}>
                    {v.label}
                  </option>
                ))}
              </NativeSelect>
            </div>
          )}
          {stacked && (
            <div className="w-full sm:w-60">
              <NativeSelect
                aria-label="Sort by"
                value={sort ? `${sort.dir === 'desc' ? '-' : ''}${sort.field}` : ''}
                onChange={(e) => go({ sort: e.target.value || undefined })}
              >
                {!sort && <option value="">Sort by</option>}
                {sort && !columns.some((c) => c.key === sort.field) && (
                  <option value={`${sort.dir === 'desc' ? '-' : ''}${sort.field}`}>
                    {fieldOf(entity, sort.field)?.label ?? sort.field},{' '}
                    {sort.dir === 'desc' ? 'descending' : 'ascending'}
                  </option>
                )}
                {columns.flatMap((c) => [
                  <option key={c.key} value={c.key}>
                    {c.label}, ascending
                  </option>,
                  <option key={`-${c.key}`} value={`-${c.key}`}>
                    {c.label}, descending
                  </option>,
                ])}
              </NativeSelect>
            </div>
          )}
          <p className="ml-auto text-xs text-slate-500">
            {list.isFetching
              ? 'Loading…'
              : `${total} ${total === 1 ? entity.label.toLowerCase() : entity.plural.toLowerCase()}`}
          </p>
        </div>
        {list.error && (
          <div className="p-3">
            <ErrorBanner>{errorMessage(list.error)}</ErrorBanner>
          </div>
        )}
        {list.isLoading ? (
          <Spinner />
        ) : (
          <RecordTable
            entity={entity}
            records={(list.data?.records ?? []) as ScreenRecord[]}
            titles={list.data?.titles ?? {}}
            columns={columns}
            sort={sort}
            onStackedChange={setStacked}
            onSort={(field) =>
              go({ sort: sort?.field === field && sort.dir === 'asc' ? `-${field}` : field })
            }
            empty={
              search.q || view || search.state
                ? 'Nothing matches'
                : `No ${entity.plural.toLowerCase()} yet`
            }
          />
        )}
        {total > PAGE && (
          <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2 text-sm text-slate-600">
            <span>
              {(page - 1) * PAGE + 1} to {Math.min(page * PAGE, total)} of {total}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                className={buttonClass('secondary', 'sm')}
                disabled={page <= 1}
                onClick={() => go({ page: page - 1 })}
              >
                Previous
              </button>
              <button
                type="button"
                className={buttonClass('secondary', 'sm')}
                disabled={page * PAGE >= total}
                onClick={() => go({ page: page + 1 })}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </Card>
    </>
  );
}
